/**
 * Synthetic user message when the user removes one filter chip.
 * Keeps other filters from that search; never defaults missing location to Colombo.
 */

const normalizeAmenities = (filters) => (
  Array.isArray(filters.amenities)
    ? filters.amenities.map((a) => String(a).trim()).filter(Boolean)
    : []
);

/** e.g. " with WiFi", " with WiFi and Coffee", " with A, B and C" */
const formatAmenitiesPhrase = (names) => {
  if (!names.length) return '';
  if (names.length === 1) return ` with ${names[0]}`;
  if (names.length === 2) return ` with ${names[0]} and ${names[1]}`;
  return ` with ${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

const formatRadiusScope = (filters) => {
  const radius = filters.radiusKm != null && Number(filters.radiusKm) > 0
    ? Number(filters.radiusKm)
    : null;
  const anchor = (filters.locationAnchor || filters.location || '').trim();
  if (radius && anchor) {
    return `within ${radius} km of ${anchor}`;
  }
  if (anchor) return `in ${anchor}`;
  return 'across Sri Lanka';
};

const buildScopedSearchPhrase = (filters, amenityNames = normalizeAmenities(filters)) => {
  const type = filters.spaceType?.trim() || null;
  const scope = formatRadiusScope(filters);
  const budget = filters.maxPrice
    ? ` under LKR ${Number(filters.maxPrice).toLocaleString()}`
    : '';
  const datePart = filters.date ? ` on ${filters.date}` : '';
  const timePart = filters.timeRange ? ` between ${filters.timeRange}` : '';
  const peoplePart = filters.peopleCount && Number(filters.peopleCount) > 1
    ? ` for ${filters.peopleCount}+ people`
    : '';
  const amenities = formatAmenitiesPhrase(amenityNames);

  if (type) return `show ${type} ${scope}${budget}${datePart}${timePart}${peoplePart}${amenities}`;
  return `show spaces ${scope}${budget}${datePart}${timePart}${peoplePart}${amenities}`;
};

/**
 * @param {'spaceType'|'location'|'radius'|'date'|'time'|'peopleCount'|'maxPrice'|'amenity'|'amenities'} removeKey
 * @param {object} filters - msg.filters from the agent search result
 * @param {{ amenityName?: string }} [options] - amenityName required when removeKey is 'amenity'
 * @returns {string}
 */
export const buildChipRemovalMessage = (removeKey, filters = {}, options = {}) => {
  const type = filters.spaceType?.trim() || null;
  const loc = filters.location?.trim() || filters.locationAnchor?.trim() || null;
  const hasRadius = filters.radiusKm != null && Number(filters.radiusKm) > 0;
  const budget = filters.maxPrice
    ? ` under LKR ${Number(filters.maxPrice).toLocaleString()}`
    : '';

  switch (removeKey) {
    case 'amenity': {
      const name = options.amenityName?.trim();
      if (!name) return '';
      const remaining = normalizeAmenities(filters).filter(
        (a) => a.toLowerCase() !== name.toLowerCase(),
      );
      return buildScopedSearchPhrase(filters, remaining);
    }
    case 'amenities':
      return buildScopedSearchPhrase(filters, []);
    case 'maxPrice':
      return 'search without a budget limit';
    case 'peopleCount':
      return 'search without a minimum capacity';
    case 'date':
      if (type) return `show ${type} ${formatRadiusScope(filters)}${budget} without a specific date`;
      return `show spaces ${formatRadiusScope(filters)}${budget} without a specific date`;
    case 'time':
      return buildScopedSearchPhrase({ ...filters, timeRange: null, startTime: null, endTime: null });
    case 'radius': {
      const withoutRadius = {
        ...filters,
        radiusKm: null,
        radiusMiles: null,
      };
      return buildScopedSearchPhrase(withoutRadius);
    }
    case 'location':
      if (hasRadius) {
        if (type) return `show ${type} across Sri Lanka${budget}`;
        return `show spaces across Sri Lanka${budget}`;
      }
      if (type) return `show ${type} across Sri Lanka${budget}`;
      return `show spaces across Sri Lanka${budget}`;
    case 'spaceType':
      if (hasRadius && loc) {
        return `show all space types within ${filters.radiusKm} km of ${loc}${budget}`;
      }
      if (loc) return `show all space types in ${loc}${budget}`;
      return `show all space types across Sri Lanka${budget}`;
    default:
      return '';
  }
};

export const formatRadiusFilterLabel = (filters) => {
  const radius = filters?.radiusKm != null && Number(filters.radiusKm) > 0
    ? Number(filters.radiusKm)
    : null;
  const anchor = (filters?.locationAnchor || filters?.location || '').trim();
  if (!radius || !anchor) return null;
  return `Within ${radius} km of ${anchor}`;
};
